/**
 * Cross-input pronoun recency (D67) — what "it" means next time.
 *
 * @remarks
 * The read half lives in `RuntimeState.session.recentEntityRefs` and the
 * parser's own recency view (D13); this module is the WRITE half's one rule:
 * which refs an executed input leaves behind as antecedents.
 *
 * @packageDocumentation
 */

import type { EntityRef, ParsedCommand } from '@orpheus/parser-engine';

/**
 * The pronoun antecedents one executed input leaves behind (D67).
 *
 * @remarks
 * Most recent first: the LAST clause's refs lead, and within a clause the
 * direct object outranks the bound phrase outranks the tool — the thing the
 * sentence was about is the thing "it" means. The room and the hands are
 * excluded on purpose: English pronouns reach for THINGS, and "search the
 * room then take it" taking the room is a reading nobody meant. Capped so the
 * view stays a working memory rather than a history.
 *
 * @param commands - The input's parsed clauses, in execution order
 * @returns The refs to remember, deduplicated, most recent first
 */
export const recentRefsOf = (commands: readonly ParsedCommand[]): readonly string[] => {
	const orderedRefs = [...commands]
		.reverse()
		.flatMap((command) => [command.directObjectRef, command.indirectObjectRef, command.toolRef])
		.filter((entityRef): entityRef is EntityRef => entityRef !== undefined)
		.map((entityRef) => entityRef as unknown as string)
		.filter((refText) => !refText.startsWith('room-ref:') && !refText.startsWith('body-ref:'));

	return [...new Set(orderedRefs)].slice(0, 4);
};
